import { Suspense, lazy } from 'react';
import { Route, Switch } from 'wouter';
import { Toaster } from 'sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ThemeToggle } from '@/components/ThemeToggle';
import { RouteGuard } from '@/components/RouteGuard';
import { useTheme } from '@/hooks/useTheme';

const LandingPage = lazy(() => import('@/pages/LandingPage'));
const DiscoveryPage = lazy(() => import('@/pages/DiscoveryPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const TutorProfilePage = lazy(() => import('@/pages/TutorProfilePage'));
const StudentDashboard = lazy(() => import('@/pages/StudentDashboard'));
const TutorDashboard = lazy(() => import('@/pages/TutorDashboard'));
const ReviewPage = lazy(() => import('@/pages/ReviewPage'));
const EditTutorProfile = lazy(() => import('@/pages/EditTutorProfile'));
const EditStudentProfile = lazy(() => import('@/pages/EditStudentProfile'));
const TeachingCalendar = lazy(() => import('@/pages/TeachingCalendar'));
const TeachingEarnings = lazy(() => import('@/pages/TeachingEarnings'));
const MessagesPage = lazy(() => import('@/pages/MessagesPage'));
const StudentBookingsPage = lazy(() => import('@/pages/StudentBookingsPage'));
const StudentProgressPage = lazy(() => import('@/pages/StudentProgressPage'));
const StudentPaymentsPage = lazy(() => import('@/pages/StudentPaymentsPage'));
const StudentSavedTutorsPage = lazy(() => import('@/pages/StudentSavedTutorsPage'));
const TutorSessionRequestsPage = lazy(() => import('@/pages/TutorSessionRequestsPage'));
const TutorStudentsPage = lazy(() => import('@/pages/TutorStudentsPage'));
const TutorReviewsPage = lazy(() => import('@/pages/TutorReviewsPage'));
const TutorServicesPage = lazy(() => import('@/pages/TutorServicesPage'));
const RegionalSEOPage = lazy(() => import('@/pages/RegionalSEOPage'));

function App() {
  const { isDark, toggleTheme } = useTheme();

  return (
    <>
      <ErrorBoundary>
        <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center text-text-dark">Loading…</div>}>
        <Switch>
          <Route path="/">
            <LandingPage />
          </Route>
          <Route path="/login">
            <RouteGuard publicOnly>
              <LoginPage />
            </RouteGuard>
          </Route>
          <Route path="/signup">
            <RouteGuard publicOnly>
              <SignupPage />
            </RouteGuard>
          </Route>
            <Route path="/forgot-password">
              <RouteGuard publicOnly>
                <ForgotPasswordPage />
              </RouteGuard>
            </Route>
            <Route path="/reset-password">
              <ResetPasswordPage />
            </Route>
        <Route path="/discover">
          <DiscoveryPage />
        </Route>
          <Route path="/online-tutoring-usa">
            <RegionalSEOPage audience="student" region="usa" />
          </Route>
          <Route path="/online-tutoring-canada">
            <RegionalSEOPage audience="student" region="canada" />
          </Route>
          <Route path="/online-tutoring-hyderabad">
            <RegionalSEOPage audience="student" region="hyderabad" />
          </Route>
          <Route path="/teach-online-usa">
            <RegionalSEOPage audience="tutor" region="usa" />
          </Route>
          <Route path="/teach-online-canada">
            <RegionalSEOPage audience="tutor" region="canada" />
          </Route>
          <Route path="/teach-online-hyderabad">
            <RegionalSEOPage audience="tutor" region="hyderabad" />
          </Route>
        <Route path="/tutors/:id">
          <TutorProfilePage />
        </Route>
        <Route path="/tutors">
          <DiscoveryPage />
        </Route>
        <Route path="/student/dashboard">
          <RouteGuard allowedRoles={['student']}>
            <StudentDashboard />
          </RouteGuard>
        </Route>
        <Route path="/tutor/dashboard">
          <RouteGuard allowedRoles={['tutor']}>
            <TutorDashboard />
          </RouteGuard>
        </Route>
        <Route path="/tutor/reviews/new">
          <RouteGuard allowedRoles={['student']}>
            <ReviewPage />
          </RouteGuard>
        </Route>
        <Route path="/tutor/edit-profile">
          <RouteGuard allowedRoles={['tutor']}>
            <EditTutorProfile />
          </RouteGuard>
        </Route>
        <Route path="/student/edit-profile">
          <RouteGuard allowedRoles={['student']}>
            <EditStudentProfile />
          </RouteGuard>
        </Route>
        <Route path="/tutor/calendar">
          <RouteGuard allowedRoles={['tutor']}>
            <TeachingCalendar />
          </RouteGuard>
        </Route>
        <Route path="/tutor/earnings">
          <RouteGuard allowedRoles={['tutor']}>
            <TeachingEarnings />
          </RouteGuard>
        </Route>
        <Route path="/tutor/requests">
          <RouteGuard allowedRoles={['tutor']}>
            <TutorSessionRequestsPage />
          </RouteGuard>
        </Route>
        <Route path="/tutor/students">
          <RouteGuard allowedRoles={['tutor']}>
            <TutorStudentsPage />
          </RouteGuard>
        </Route>
        <Route path="/tutor/reviews">
          <RouteGuard allowedRoles={['tutor']}>
            <TutorReviewsPage />
          </RouteGuard>
        </Route>
        <Route path="/tutor/services">
          <RouteGuard allowedRoles={['tutor']}>
            <TutorServicesPage />
          </RouteGuard>
        </Route>
        <Route path="/tutor/messages">
          <RouteGuard allowedRoles={['tutor']}>
            <MessagesPage />
          </RouteGuard>
        </Route>
        <Route path="/student/bookings">
          <RouteGuard allowedRoles={['student']}>
            <StudentBookingsPage />
          </RouteGuard>
        </Route>
        <Route path="/student/progress">
          <RouteGuard allowedRoles={['student']}>
            <StudentProgressPage />
          </RouteGuard>
        </Route>
        <Route path="/student/saved-tutors">
          <RouteGuard allowedRoles={['student']}>
            <StudentSavedTutorsPage />
          </RouteGuard>
        </Route>
        <Route path="/student/payments">
          <RouteGuard allowedRoles={['student']}>
            <StudentPaymentsPage />
          </RouteGuard>
        </Route>
        <Route path="/student/calendar">
          <RouteGuard allowedRoles={['student']}>
            <StudentBookingsPage />
          </RouteGuard>
        </Route>
        <Route path="/student/earnings">
          <RouteGuard allowedRoles={['student']}>
            <StudentPaymentsPage />
          </RouteGuard>
        </Route>
        <Route path="/student/messages">
          <RouteGuard allowedRoles={['student']}>
            <MessagesPage />
          </RouteGuard>
        </Route>
        <Route>
          <div className="min-h-screen flex items-center justify-center">
            <h2 className="text-2xl font-bold">404 - Page Not Found</h2>
          </div>
        </Route>
      </Switch>
      </Suspense>
      </ErrorBoundary>
      <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
      <Toaster
        position="top-right"
        richColors
        theme={isDark ? 'dark' : 'light'}
        toastOptions={{
          classNames: {
            toast: 'sonner-toaster rounded-3xl border border-border bg-card-background text-text-dark shadow-lg',
            title: 'text-sm font-semibold text-heading',
            description: 'text-sm text-text-light',
            actionButton: 'bg-primary text-white',
            cancelButton: 'bg-background text-text-dark border border-border',
          },
        }}
      />
    </>
  );
}

export default App;
